import './Stats.scss';
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import AuthContext from "../../context/AuthContext";
import { API_BASE_URL } from '../../config';
import { authHeaders } from '../../utils/api';
import { formatDate } from '../../utils/dates';
import { COUNTRY_BY_CODE } from '../Profile/countries';
import { ChartCard, TimeSeries, SeriesTable, BarList, Columns, StatTile, fmtNum } from './charts';
import WriterPanel, { BooksTable, SortToggle } from './WriterPanel';

// Private: /stats isn't linked anywhere. The API is the real gate (staff only);
// this page just explains itself when the account isn't allowed.

const RANGES = [
    { days: 7, label: '٧ أيام' },
    { days: 30, label: '٣٠ يومًا' },
    { days: 90, label: '٩٠ يومًا' },
    { days: 365, label: 'سنة' },
];

const countryName = (code) => COUNTRY_BY_CODE[code] || code || 'غير محدد';
const sum = (series) => series.reduce((a, p) => a + p.value, 0);

export default function Stats() {
    const { authTokens } = useContext(AuthContext);
    const [days, setDays] = useState(90);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [bookSort, setBookSort] = useState('views');
    const [writer, setWriter] = useState(null);     // username open in the side panel
    const closeWriter = useCallback(() => setWriter(null), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const r = await fetch(`${API_BASE_URL}/api/stats/?days=${days}`, { headers: authHeaders(authTokens) });
                if (r.status === 403 || r.status === 401) { if (!cancelled) setError('forbidden'); return; }
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const d = await r.json();
                if (!cancelled) { setData(d); setError(null); }
            } catch {
                if (!cancelled) setError('failed');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [days, authTokens]);

    const derived = useMemo(() => {
        if (!data) return null;
        const s = data.series;
        return {
            signupsInRange: sum(s.signups),
            publishesInRange: sum(s.publishes),
            readsInRange: sum(s.reads),
            activePeak: Math.max(0, ...s.active_users.map(p => p.value)),
            spark: (series) => series.slice(-12).map(p => p.value),
            countries: data.countries.map(c => ({ label: countryName(c.code), value: c.count })),
            ratings: Object.entries(data.rating_distribution).map(([k, v]) => ({ label: '★'.repeat(Number(k)), value: v })),
        };
    }, [data]);

    if (error === 'forbidden') {
        return (
            <>
            <Navbar />
            <main className='st-page'><div className='st-container'>
                <p className='st-empty'>هذه الصفحة متاحة لمشرفي راوية فقط.</p>
            </div></main>
            <Footer />
            </>
        );
    }

    return (
        <>
        <Navbar />
        <main className='st-page'>
            <div className='st-container'>
                <header className='st-header'>
                    <div>
                        <h1>إحصاءات راوية</h1>
                        {data && <p>آخر تحديث {new Date(data.generated_at).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })} · الأرقام تُحدَّث كل دقيقتين</p>}
                    </div>
                    {/* the one filter row; everything below is scoped by it */}
                    <div className='st-ranges' role='group' aria-label='الفترة'>
                        {RANGES.map(r => (
                            <button key={r.days} type='button' className={days === r.days ? 'is-active' : ''}
                                    onClick={() => setDays(r.days)} aria-pressed={days === r.days}>
                                {r.label}
                            </button>
                        ))}
                    </div>
                </header>

                {error === 'failed' && <p className='st-empty'>تعذر تحميل الإحصاءات.</p>}

                {data && derived && (
                    // refetch keeps the frame: previous render stays, dimmed
                    <div className={'st-body' + (loading ? ' is-loading' : '')}>
                        <section className='st-tiles'>
                            <StatTile label='الأعضاء' value={data.totals.users} delta={derived.signupsInRange} spark={derived.spark(data.series.users_cumulative)} />
                            <StatTile label='كتب منشورة' value={data.totals.books_published} delta={derived.publishesInRange} spark={derived.spark(data.series.books_cumulative)} />
                            <StatTile label='القراءات' value={data.totals.views} delta={derived.readsInRange} spark={derived.spark(data.series.reads)} />
                            <StatTile label='التقييمات' value={data.totals.ratings} />
                            <StatTile label='العلامات والتظليلات' value={data.totals.bookmarks + data.totals.highlights} />
                            <StatTile label='كلمات منشورة' value={data.totals.words} />
                        </section>

                        <section className='st-grid'>
                            <ChartCard title='أعضاء جدد يوميًا' subtitle={`${fmtNum(derived.signupsInRange)} في الفترة · ${fmtNum(data.totals.users_unverified)} لم يؤكدوا بريدهم بعد`}
                                       table={<SeriesTable data={data.series.signups} label='أعضاء جدد' />}>
                                <TimeSeries data={data.series.signups} />
                            </ChartCard>
                            <ChartCard title='إجمالي الأعضاء' subtitle='تراكمي'
                                       table={<SeriesTable data={data.series.users_cumulative} label='الإجمالي' />}>
                                <TimeSeries data={data.series.users_cumulative} />
                            </ChartCard>
                            <ChartCard title='كتب نُشرت يوميًا' subtitle={`${fmtNum(derived.publishesInRange)} في الفترة · ${fmtNum(data.totals.books_drafts)} مسودة غير منشورة`}
                                       table={<SeriesTable data={data.series.publishes} label='كتب' />}>
                                <TimeSeries data={data.series.publishes} />
                            </ChartCard>
                            <ChartCard title='القراءات يوميًا' subtitle='قراءة واحدة لكل قارئ لكل كتاب في اليوم · يبدأ العدّ من تاريخ تشغيل هذه الصفحة'
                                       table={<SeriesTable data={data.series.reads} label='قراءات' />}>
                                <TimeSeries data={data.series.reads} />
                            </ChartCard>
                            <ChartCard title='أعضاء نشطون يوميًا' subtitle={`سجّلوا الدخول في ذلك اليوم · الذروة ${fmtNum(derived.activePeak)}`}
                                       table={<SeriesTable data={data.series.active_users} label='أعضاء' />}>
                                <TimeSeries data={data.series.active_users} />
                            </ChartCard>
                            <ChartCard title='توزيع التقييمات' subtitle={`المتوسط ${data.totals.avg_rating.toLocaleString('ar-EG')} من ٥`}>
                                <Columns data={derived.ratings} />
                            </ChartCard>
                            <ChartCard title='الأعضاء حسب الدولة' subtitle='من الملفات التي حُدِّد فيها البلد'>
                                <BarList data={derived.countries} />
                            </ChartCard>
                            <ChartCard title='النقاشات' subtitle='المنتدى'>
                                <div className='st-pair'>
                                    <div><span className='st-pair-value'>{fmtNum(data.totals.topics)}</span><span className='st-pair-label'>موضوعًا</span></div>
                                    <div><span className='st-pair-value'>{fmtNum(data.totals.comments)}</span><span className='st-pair-label'>تعليقًا</span></div>
                                </div>
                            </ChartCard>
                        </section>

                        <section className='st-card'>
                            <header className='st-card-head'>
                                <div><h3>{bookSort === 'rating' ? 'الكتب الأعلى تقييمًا' : 'الكتب الأكثر قراءة'}</h3><p>اضغط على اسم الكاتب لعرض ملفه وكتبه</p></div>
                                <SortToggle value={bookSort} onChange={setBookSort} />
                            </header>
                            <BooksTable books={bookSort === 'rating' ? data.top_rated : data.top_books} onAuthor={setWriter} />
                        </section>

                        <section className='st-card'>
                            <header className='st-card-head'><div><h3>آخر المنضمّين</h3></div></header>
                            <div className='st-table-wrap'>
                                <table className='st-table'>
                                    <thead><tr><th>المستخدم</th><th>الدولة</th><th>انضم</th><th>البريد</th><th>الدخول</th><th>كتب (منشورة)</th></tr></thead>
                                    <tbody>
                                        {data.recent_users.map(u => (
                                            <tr key={u.username}>
                                                <td><button type='button' className='st-link' onClick={() => setWriter(u.username)}>{u.username}</button></td>
                                                <td>{countryName(u.country)}</td>
                                                <td>{formatDate(u.joined)}</td>
                                                <td>{u.verified ? 'مؤكد' : <span className='st-warn'>غير مؤكد</span>}</td>
                                                <td>{u.provider === 'google' ? 'Google' : 'كلمة مرور'}</td>
                                                <td>{fmtNum(u.books)} ({fmtNum(u.published)})</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                )}

                {!data && loading && <p className='st-empty'>جارٍ التحميل…</p>}
            </div>
        </main>
        {writer && <WriterPanel username={writer} authTokens={authTokens} onClose={closeWriter} />}
        <Footer />
        </>
    );
}
